/* import __COLOCATED_TEMPLATE__ from './fin-live-banner.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type AIAgentSetupService from 'embercom/services/ai-agent-setup-service';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import type Store from '@ember-data/store';
import {
  CONVERSATION_STARTED_TARGET,
  NEW_CONVERSATION_TARGET,
} from 'embercom/lib/operator/custom-bots/constants';
import type WorkflowCreationService from 'embercom/services/workflow-creation-service';
import { type ChatChannel } from 'embercom/services/workflow-creation-service';

interface Signature {
  Element: HTMLDivElement;
}

export default class FinLiveBanner extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare aiAgentSetupService: AIAgentSetupService;
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare workflowCreationService: WorkflowCreationService;

  @tracked isModalOpen = false;
  @tracked selectedChatChannel: ChatChannel = 'messenger';

  get channelType() {
    return this.aiAgentSetupService.channelType;
  }

  get title() {
    return this.channelType === 'email'
      ? this.intl.t('operator.fin.setup.fin-live-banner.title.email')
      : this.intl.t('operator.fin.setup.fin-live-banner.title.chat');
  }

  get description() {
    return this.channelType === 'email'
      ? this.intl.t('operator.fin.setup.fin-live-banner.description.email')
      : this.intl.t('operator.fin.setup.fin-live-banner.description.chat');
  }

  get workflowTemplate() {
    if (this.channelType === 'email') {
      return this.workflowCreationService.finWorkflowTemplateForEmail;
    } else {
      return this.workflowCreationService.finWorkflowTemplateForChat[this.selectedChatChannel];
    }
  }

  get workflowTemplateContentObject() {
    if (!this.workflowTemplate) {
      return;
    }
    return this.workflowTemplate?.ruleset?.rulesetLinks.firstObject?.object;
  }

  @action
  createWorkflowFromTemplate(e: Event) {
    e.stopPropagation();
    if (!this.workflowTemplate) {
      return;
    }
    return this.workflowCreationService.createWorkflowFromTemplate(this.workflowTemplate);
  }

  @action
  createBlankWorkflow(e: Event) {
    e.stopPropagation();
    if (this.channelType === 'email') {
      return this.workflowCreationService.createWorkflowFromScratch({
        target: CONVERSATION_STARTED_TARGET,
        targetChannels: ['email'],
      });
    }

    switch (this.selectedChatChannel) {
      case 'messenger':
        return this.workflowCreationService.createWorkflowFromScratch({
          target: NEW_CONVERSATION_TARGET,
          targetChannels: ['web', 'android', 'ios'],
        });
      case 'whatsapp':
        return this.workflowCreationService.createWorkflowFromScratch({
          target: CONVERSATION_STARTED_TARGET,
          targetChannels: ['whatsapp'],
        });
      case 'slack':
        return this.workflowCreationService.createWorkflowFromScratch({
          target: CONVERSATION_STARTED_TARGET,
          targetChannels: ['slack'],
        });
      case 'facebook':
        return this.workflowCreationService.createWorkflowFromScratch({
          target: CONVERSATION_STARTED_TARGET,
          targetChannels: ['facebook'],
        });
      case 'instagram':
        return this.workflowCreationService.createWorkflowFromScratch({
          target: CONVERSATION_STARTED_TARGET,
          targetChannels: ['instagram'],
        });
      case 'sms':
        return this.workflowCreationService.createWorkflowFromScratch({
          target: CONVERSATION_STARTED_TARGET,
          targetChannels: ['sms'],
        });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::FinLiveBanner': typeof FinLiveBanner;
    'operator/fin/setup/fin-live-banner': typeof FinLiveBanner;
  }
}
